import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import JSZip from 'jszip';
import html2canvas from 'html2canvas';
import { 
  ArrowLeft, Upload, Download, FileSpreadsheet, Info, Zap, 
  TrendingUp, AlertTriangle, Calendar, BarChart3, Save, History, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, ReferenceLine, Legend 
} from 'recharts';
import { parseElectricityData, analyzeData, generateTemplate } from '@/lib/piekvermoigenParser';
import { usePiekanalyses } from '@/hooks/usePiekanalyses';
import { AnalysisMetadataForm } from '@/components/piekanalyse';
import type { AnalysisConfig, AnalysisResult } from '@/types/piekanalyse';

const DUTCH_MONTHS = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
];

const CRMPiekvermogen: React.FC = () => {
  const navigate = useNavigate();
  const { analyses, isLoading: analysesLoading, createAnalysis } = usePiekanalyses();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chartsRef = useRef<HTMLDivElement>(null);

  const [config, setConfig] = useState<AnalysisConfig>({
    dataType: 'kWp',
    includeReturnDelivery: false,
    contractedCapacity: 235,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [analysisName, setAnalysisName] = useState('');
  
  // New: organization and year selection
  const [selectedOrganization, setSelectedOrganization] = useState<{ id: string; name: string } | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | undefined>();

  // Calculate global max for consistent Y-axis (rounded to nearest 10)
  const rawMax = result ? Math.max(
    result.highestPeakKWp,
    config.contractedCapacity,
    ...(result.hasReturnDelivery ? result.yearOverview.map(d => d.maxReturnKWp || 0) : [])
  ) * 1.1 : 0;
  const globalMax = Math.ceil(rawMax / 10) * 10;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setFileName(file.name);
    setAnalysisName(file.name.replace(/\.[^/.]+$/, ''));

    try {
      const data = await parseElectricityData(file, config);
      const analysisResult = analyzeData(data, config);
      setResult(analysisResult);
      
      toast.success(`${data.length} datapunten geanalyseerd${analysisResult.hasReturnDelivery ? ', teruglevering gedetecteerd' : ''}`);
    } catch (error) {
      console.error('Parse error:', error);
      toast.error('Kon bestand niet verwerken. Controleer het formaat.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAnalysis = async () => {
    if (!result || !analysisName.trim()) {
      toast.error('Geef eerst een naam op voor de analyse');
      return;
    }

    await createAnalysis.mutateAsync({
      name: analysisName,
      config,
      result,
      organizationId: selectedOrganization?.id,
      analysisYear: selectedYear || result.detectedYear,
    });

    navigate('/crm/analyses');
  };

  const handleExportPNG = async (elementId: string, filename: string) => {
    const element = document.getElementById(elementId);
    if (!element) return;

    try {
      const canvas = await html2canvas(element, { 
        backgroundColor: '#ffffff',
        scale: 2, // Higher quality
      });
      
      // Use toBlob for proper download (not toDataURL which opens in new tab)
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('Export mislukt');
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `${filename}.png`;
        link.href = url;
        // Append to DOM for browser compatibility
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('Grafiek geëxporteerd');
      }, 'image/png');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export mislukt');
    }
  };

  const handleExportZip = async () => {
    if (!chartsRef.current) return;

    try {
      toast.info('ZIP export gestart...');
      const zip = new JSZip();
      const charts = chartsRef.current.querySelectorAll('[data-chart]');
      
      for (const chart of charts) {
        const name = chart.getAttribute('data-chart');
        const canvas = await html2canvas(chart as HTMLElement, { 
          backgroundColor: '#ffffff',
          scale: 2, // Higher quality
        });
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((b) => {
            if (b) resolve(b);
            else reject(new Error('Failed to create blob'));
          }, 'image/png');
        });
        zip.file(`${name}.png`, blob);
      }

      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.download = `piekvermogen_analyse_${analysisName || 'export'}.zip`;
      link.href = url;
      // Append to DOM for browser compatibility
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Alle grafieken geëxporteerd (${charts.length} bestanden)`);
    } catch (error) {
      console.error('ZIP export error:', error);
      toast.error('ZIP export mislukt');
    }
  };

  const handleNewAnalysis = () => {
    setResult(null);
    setFileName('');
    setAnalysisName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-popover border rounded-lg p-3 shadow-lg">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.value?.toFixed(2)} kWp
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/crm/analyses')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="h-6 w-6" />
            Piekvermogen Analyse
          </h1>
          <p className="text-muted-foreground">
            Analyseer kwartierdata om piekverbruiken te visualiseren
          </p>
        </div>
        {result && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportZip}>
              <Download className="h-4 w-4 mr-2" />
              ZIP Export
            </Button>
            <Button onClick={handleSaveAnalysis} disabled={createAnalysis.isPending}>
              <Save className="h-4 w-4 mr-2" />
              Opslaan
            </Button>
          </div>
        )}
      </div>

      {!result ? (
        <>
          {/* Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Data Type */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Data Type</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>kWh data</Label>
                  <Switch
                    checked={config.dataType === 'kWh'}
                    onCheckedChange={(checked) => 
                      setConfig(c => ({ ...c, dataType: checked ? 'kWh' : 'kWp' }))
                    }
                  />
                </div>
                {config.dataType === 'kWh' && (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      kWh wordt geconverteerd naar kWp (×4)
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Return Delivery */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Teruglevering</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Teruglevering meenemen</Label>
                  <Switch
                    checked={config.includeReturnDelivery}
                    onCheckedChange={(checked) => 
                      setConfig(c => ({ ...c, includeReturnDelivery: checked }))
                    }
                  />
                </div>
                {config.includeReturnDelivery && (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Kolom D wordt gelezen als teruglevering
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Contracted Capacity */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Gecontracteerd Vermogen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={config.contractedCapacity}
                    onChange={(e) => setConfig(c => ({ ...c, contractedCapacity: Number(e.target.value) }))}
                    className="flex-1"
                  />
                  <span className="text-muted-foreground">kWp</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Excel Upload
              </CardTitle>
              <CardDescription>
                Upload een Excel bestand met kwartierdata
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {[2024, 2025, 2026].map((year) => (
                  <Button
                    key={year}
                    variant="outline"
                    size="sm"
                    asChild
                  >
                    <a href={`/templates/CRM_Template_${year}.xlsx`} download={`CRM_Template_${year}.xlsx`}>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Template {year}
                    </a>
                  </Button>
                ))}
              </div>

              <div 
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                {isLoading ? (
                  <div className="animate-pulse">Bestand verwerken...</div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">
                      Klik om een bestand te selecteren of sleep het hierheen
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Ondersteunde formaten: .xlsx, .xls, .csv
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Analysis History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Analyse Geschiedenis
              </CardTitle>
              <CardDescription>
                Eerder opgeslagen piekvermogen analyses
              </CardDescription>
            </CardHeader>
            <CardContent>
              {analysesLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : analyses.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Nog geen analyses opgeslagen
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Naam</TableHead>
                      <TableHead>Organisatie</TableHead>
                      <TableHead>Jaar</TableHead>
                      <TableHead>Hoogste Piek</TableHead>
                      <TableHead>Overschrijdingen</TableHead>
                      <TableHead>Aangemaakt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analyses.slice(0, 10).map((analysis) => (
                      <TableRow 
                        key={analysis.id}
                        className="cursor-pointer hover:bg-muted"
                        onClick={() => navigate(`/crm/analyses/piekvermogen/${analysis.id}`)}
                      >
                        <TableCell className="font-medium">{analysis.name}</TableCell>
                        <TableCell>
                          {analysis.organization_name ? (
                            <Badge variant="outline" className="font-normal">
                              <Building2 className="h-3 w-3 mr-1" />
                              {analysis.organization_name}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {analysis.analysis_year || <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>{analysis.highest_peak_kwp} kWp</TableCell>
                        <TableCell>{analysis.total_exceedances}</TableCell>
                        <TableCell>
                          {format(new Date(analysis.created_at), 'd MMM yyyy', { locale: nl })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <div ref={chartsRef} className="space-y-6">
          {/* Analysis Metadata Form - Name, Organization, Year */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4">
            <AnalysisMetadataForm
              analysisName={analysisName}
              onNameChange={setAnalysisName}
              selectedOrganization={selectedOrganization}
              onOrganizationChange={setSelectedOrganization}
              detectedYear={result.detectedYear}
              selectedYear={selectedYear}
              onYearChange={setSelectedYear}
            />
            
            <Card className="h-fit">
              <CardContent className="pt-6">
                <Button variant="outline" onClick={handleNewAnalysis} className="w-full">
                  Nieuwe Analyse
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" id="stats" data-chart="statistieken">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-sm">Hoogste Piek</span>
                </div>
                <p className="text-2xl font-bold">{result.highestPeakKWp} kWp</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Zap className="h-4 w-4" />
                  <span className="text-sm">Contract</span>
                </div>
                <p className="text-2xl font-bold">{config.contractedCapacity} kWp</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm">Maanden Boven</span>
                </div>
                <p className="text-2xl font-bold">{result.monthsAboveContract}/12</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm">Overschrijdingen</span>
                </div>
                <p className="text-2xl font-bold">{result.totalExceedances}</p>
              </CardContent>
            </Card>
          </div>

          {/* Info Badges */}
          {(config.dataType === 'kWh' || result.hasReturnDelivery) && (
            <div className="flex gap-2 flex-wrap">
              {config.dataType === 'kWh' && (
                <Alert className="w-auto">
                  <Info className="h-4 w-4" />
                  <AlertDescription>kWh data geconverteerd naar kWp</AlertDescription>
                </Alert>
              )}
              {result.hasReturnDelivery && (
                <Alert className="w-auto">
                  <Info className="h-4 w-4" />
                  <AlertDescription>Teruglevering data gedetecteerd</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Year Overview Chart */}
          <Card id="yearChart" data-chart="jaaroverzicht">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Jaaroverzicht
                </CardTitle>
                <CardDescription>Maximale piek per maand</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleExportPNG('yearChart', 'jaaroverzicht')}>
                <Download className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={result.yearOverview}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthName" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 300]} ticks={[0, 50, 100, 150, 200, 250, 300]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <ReferenceLine 
                      y={config.contractedCapacity} 
                      stroke="hsl(var(--destructive))" 
                      strokeDasharray="5 5" 
                      label={{ value: 'Contract', position: 'right', fill: 'hsl(var(--destructive))' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="maxKWp" 
                      stroke="#014336" 
                      strokeWidth={2}
                      name="Verbruik"
                      dot={{ fill: '#014336' }}
                    />
                    {result.hasReturnDelivery && (
                      <Line 
                        type="monotone" 
                        dataKey="maxReturnKWp" 
                        stroke="#FF8C00" 
                        strokeWidth={2}
                        name="Teruglevering"
                        dot={{ fill: '#FF8C00' }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Highest Day Chart */}
          <Card id="highestDayChart" data-chart="hoogste_dag">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Hoogste Dag - Uurwaarden
                </CardTitle>
                <CardDescription>
                  {format(result.highestDay.date, 'd MMMM yyyy', { locale: nl })}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleExportPNG('highestDayChart', 'hoogste_dag')}>
                <Download className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={result.highestDay.hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hourLabel" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 300]} ticks={[0, 50, 100, 150, 200, 250, 300]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <ReferenceLine 
                      y={config.contractedCapacity} 
                      stroke="hsl(var(--destructive))" 
                      strokeDasharray="5 5"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="maxKWp" 
                      stroke="#014336" 
                      strokeWidth={2}
                      name="Verbruik"
                      dot={{ fill: '#014336' }}
                    />
                    {result.hasReturnDelivery && (
                      <Line 
                        type="monotone" 
                        dataKey="maxReturnKWp" 
                        stroke="#FF8C00" 
                        strokeWidth={2}
                        name="Teruglevering"
                        dot={{ fill: '#FF8C00' }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Charts Grid */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Maandoverzichten</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {DUTCH_MONTHS.map((monthName, monthIndex) => {
                const monthData = result.monthlyOverviews[monthIndex] || [];
                if (monthData.length === 0) return null;
                
                return (
                  <Card key={monthIndex} id={`month-${monthIndex}`} data-chart={`${monthName.toLowerCase()}_dagen`}>
                    <CardHeader className="py-3 flex flex-row items-center justify-between">
                      <CardTitle className="text-sm">{monthName}</CardTitle>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleExportPNG(`month-${monthIndex}`, `${monthName.toLowerCase()}_dagen`)}
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={monthData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="dateStr" 
                              tick={{ fontSize: 10 }} 
                              interval={0}
                              tickFormatter={(value) => {
                                const day = parseInt(value);
                                return day % 2 === 0 ? String(day) : '';
                              }}
                            />
                            <YAxis domain={[0, 300]} ticks={[0, 50, 100, 150, 200, 250, 300]} tick={{ fontSize: 10 }} />
                            <Tooltip content={<CustomTooltip />} />
                            <ReferenceLine 
                              y={config.contractedCapacity} 
                              stroke="hsl(var(--destructive))" 
                              strokeDasharray="3 3"
                            />
                            <Line 
                              type="monotone" 
                              dataKey="maxKWp" 
                              stroke="#014336" 
                              strokeWidth={1.5}
                              dot={false}
                            />
                            {result.hasReturnDelivery && (
                              <Line 
                                type="monotone" 
                                dataKey="maxReturnKWp" 
                                stroke="#FF8C00" 
                                strokeWidth={1.5}
                                dot={false}
                              />
                            )}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMPiekvermogen;
